import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CATEGORIES, categoryBySlug } from "../content/categories";
import { postsInCategory } from "../content/posts";
import { PostCard } from "../components/PostCard";

export const Route = createFileRoute("/category/$category")({
  loader: ({ params }) => {
    const cat = categoryBySlug(params.category);
    if (!cat) throw notFound();
    return { cat };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Category not found — Acadly" }, { name: "robots", content: "noindex" }],
      };
    }
    const { cat } = loaderData;
    const url = `https://acadly.in/category/${params.category}`;
    return {
      meta: [
        { title: `${cat.label} — Acadly` },
        { name: "description", content: cat.blurb },
        {
          name: "robots",
          content: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
        },
        { property: "og:site_name", content: "Acadly" },
        { property: "og:locale", content: "en_IN" },
        { property: "og:type", content: "website" },
        { property: "og:title", content: `${cat.label} — Acadly` },
        { property: "og:description", content: cat.blurb },
        { property: "og:url", content: url },
        { property: "og:image", content: "https://acadly.in/og-default.png" },
        { property: "og:image:alt", content: `${cat.label} — Acadly` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${cat.label} — Acadly` },
        { name: "twitter:description", content: cat.blurb },
        { name: "twitter:image", content: "https://acadly.in/og-default.png" },
        { name: "twitter:image:alt", content: `${cat.label} — Acadly` },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://acadly.in/" },
              {
                "@type": "ListItem",
                position: 2,
                name: cat.label,
                item: url,
              },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center">
      <h1 className="text-3xl font-semibold" style={{ fontFamily: "var(--font-display)" }}>
        No such section.
      </h1>
      <Link
        to="/"
        className="inline-block mt-6 mono-label border border-ink px-4 py-2 hover:text-grade hover:border-grade"
      >
        Home
      </Link>
    </div>
  ),
  component: CategoryPage,
});

function CategoryPage() {
  const { cat } = Route.useLoaderData();
  const posts = postsInCategory(cat.slug);

  return (
    <div className="mx-auto max-w-6xl px-5 py-16 md:py-20">
      <header className="mb-12 max-w-3xl">
        <div className="stamp mb-6">Section · {cat.label}</div>
        <h1
          className="text-5xl md:text-6xl leading-[1] tracking-tight font-semibold"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {cat.label}
        </h1>
        <p className="mt-5 text-lg text-pencil leading-relaxed">{cat.blurb}</p>
        {cat.slug === "news" && (
          <aside className="mt-8 border-l-2 border-grade bg-cream/60 px-5 py-4 text-sm text-pencil leading-relaxed">
            <div className="mono-label mb-1 text-grade">About this coverage</div>
            <p className="!m-0">
              Acadly is not an investigative newsroom. This section
              synthesises official Delhi University notifications, academic
              council minutes and reporting from established outlets into
              short, student-facing briefings. Every News post links to the
              primary sources it draws on. If a claim is not yet in an
              official DU document, we say so explicitly. Read our{" "}
              <Link to="/editorial-policy" className="underline hover:text-grade">
                editorial policy
              </Link>{" "}
              for how we verify.
            </p>
          </aside>
        )}
      </header>

      {posts.length === 0 ? (
        <p className="text-pencil">Nothing published in this section yet.</p>
      ) : (
        <div className="grid gap-10 md:grid-cols-2">
          {posts.map((p) => (
            <PostCard key={p.meta.slug} meta={p.meta} />
          ))}
        </div>
      )}

      <div className="mt-16 border-t border-rule pt-8">
        <div className="mono-label mb-3">Other sections</div>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.filter((c) => c.slug !== cat.slug).map((c) => (
            <Link
              key={c.slug}
              to="/category/$category"
              params={{ category: c.slug }}
              className="mono-label border border-rule px-3 py-1.5 hover:border-grade hover:text-grade transition-colors"
            >
              {c.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}